import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Button } from '@ui/button.tsx';
import { SelectDropdown, type SelectOption } from '@ui/select-dropdown.tsx';
import { Textarea } from '@ui/textarea.tsx';
import { ComboboxInput } from '@ui/combobox-input.tsx';
import { Calendar } from '@ui/calendar.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '@ui/popover.tsx';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { CalendarIcon, Plus } from 'lucide-react';
import { useDebounce } from '@r/hooks/use-debounce.ts';
import type { Option } from '@r/types/index.ts';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useUser } from '@r/api/auth.tsx';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { isTargetLogicWorkspace } from '@r/utils/workspace-utils.ts';
import { useProductReferenceEnabled } from '@r/hooks/use-product-reference-enabled.ts';
import {
  apiAdminperformancesPostBody,
  apiAdminperformancesPostBodyAmountMin
} from '@r/api/generated-zod/index.ts';
import {
  useApiAdmincompaniesGetCollection,
  useApiAdmincompaniesIdGet,
  useApiAdminusersGetCollection,
  useApiAdminperformancesPost
} from '@r/api/generated/client.ts';
import type {
  CompanyCompanyCollectionReadModel,
  ReferenceReferenceReadModel,
  UserUserCollectionReadModel
} from '@r/api/generated/client.schemas.ts';
import { toast } from 'sonner';
import { cn } from '@r/utils/utils.ts';
import { format } from 'date-fns';
import { toUtcStartOfDay } from '@r/utils/date-utils.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { useAllReferences } from '@r/hooks/use-all-references.ts';

interface AddPerformanceFormProps {
  onSuccess: () => void;
  fixedCompany?: { id: number; uniqueCode: string; name: string };
}

const parseNumberInput = (value: unknown): number | undefined => {
  if (typeof value === 'number') return value;
  if (typeof value !== 'string') return undefined;
  const normalized = value.replace(',', '.').trim();
  if (normalized.length === 0) return undefined;
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const getAddPerformanceSchema = (
  t: ReturnType<typeof useTranslation>['t'],
  isProductReferenceEnabled: boolean
) => {
  const { shape } = apiAdminperformancesPostBody;

  return apiAdminperformancesPostBody.extend({
    companyUniqueCode: shape.companyUniqueCode.describe(''),
    userUniqueCode: shape.userUniqueCode.describe(''),
    label: shape.label.describe(''),
    targetReference: shape.targetReference.describe(
      t('admin.performances.add.form.target.reference.info')
    ),
    referenceUniqueCode: shape.referenceUniqueCode.describe(''),
    amount: z.preprocess(
      parseNumberInput,
      (isProductReferenceEnabled
        ? z
            .number()
            .min(
              apiAdminperformancesPostBodyAmountMin,
              t('admin.performances.add.form.amount.required')
            )
            .optional()
        : z
            .number({ required_error: t('admin.performances.add.form.amount.required') })
            .min(
              apiAdminperformancesPostBodyAmountMin,
              t('admin.performances.add.form.amount.required')
            )
      ).describe(t('admin.performances.add.form.amount.info'))
    ),
    referenceQuantity: z.preprocess(parseNumberInput, shape.referenceQuantity.describe('')),
    realizedAt: z
      .date({ required_error: t('admin.performances.add.form.realized.at.required') })
      .describe(t('admin.performances.add.form.realized.at.info'))
  });
};

export const AddPerformanceForm: FC<AddPerformanceFormProps> = ({ onSuccess, fixedCompany }) => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const user = useUser();
  const { workspace, isWorkspaceSuccess } = UseWorkspace(user.data);
  const isProductReferenceEnabled = useProductReferenceEnabled();
  const isTargetLogic = useMemo(
    () => isWorkspaceSuccess && isTargetLogicWorkspace(workspace),
    [isWorkspaceSuccess, workspace]
  );

  const [companyQuery, setCompanyQuery] = useState('');
  const debouncedCompanyQuery = useDebounce(companyQuery, 500);
  const [selectedCompanyOption, setSelectedCompanyOption] = useState<Option | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const isCompanyFixed = Boolean(fixedCompany);

  const companiesQuery = useApiAdmincompaniesGetCollection(
    !isCompanyFixed && debouncedCompanyQuery && debouncedCompanyQuery.length > 0
      ? { search: debouncedCompanyQuery, perPage: 50 }
      : { 'order[name]': 'asc' as const }
  );

  const companyOptions: Option[] = useMemo(
    () =>
      fixedCompany
        ? [{ label: fixedCompany.name, value: fixedCompany.uniqueCode }]
        : (companiesQuery.data?.data ?? []).map((company: CompanyCompanyCollectionReadModel) => ({
            label: company.name,
            value: company.uniqueCode
          })),
    [companiesQuery.data?.data, fixedCompany]
  );

  const selectedCompanyName = selectedCompanyOption?.label ?? '';

  const companyDetailQuery = useApiAdmincompaniesIdGet(
    selectedCompanyId != null ? String(selectedCompanyId) : null,
    { query: { enabled: selectedCompanyId != null } }
  );

  const usersQuery = useApiAdminusersGetCollection(
    selectedCompanyName
      ? {
          'userWorkspaces.company.name': selectedCompanyName,
          'maxRole[]': ['ROLE_MEMBER', 'ROLE_MANAGER'] as const,
          perPage: 500
        }
      : undefined,
    { query: { enabled: selectedCompanyName.length > 0 } }
  );

  const referencesQuery = useAllReferences(undefined, { enabled: isProductReferenceEnabled });

  const targetOptions = useMemo(() => {
    const statuses = companyDetailQuery.data?.targetStatuses ?? [];
    return statuses
      .map((s) => s.logicTypeTarget?.reference)
      .filter((ref): ref is string => Boolean(ref));
  }, [companyDetailQuery.data?.targetStatuses]);

  const targetSelectOptions: SelectOption[] = useMemo(
    () =>
      targetOptions.map((ref) => ({
        value: ref,
        label: ref
      })),
    [targetOptions]
  );

  const beneficiaryOptions: Option[] = useMemo(() => {
    const users = (usersQuery.data?.data ?? []) as UserUserCollectionReadModel[];
    return users.map((u) => {
      const uw = u.userWorkspaces?.find(
        (w) => w.company?.uniqueCode === selectedCompanyOption?.value
      );
      const uniqueCode = uw?.uniqueCode ?? u.userWorkspaces?.[0]?.uniqueCode ?? '';
      return { label: u.fullname, value: uniqueCode };
    });
  }, [usersQuery.data?.data, selectedCompanyOption?.value]);

  const beneficiarySelectOptions: SelectOption[] = useMemo(
    () =>
      beneficiaryOptions.map((opt) => ({
        value: opt.value,
        label: opt.label
      })),
    [beneficiaryOptions]
  );

  const referenceOptions: Option[] = useMemo(
    () =>
      (referencesQuery.data ?? []).map((ref: ReferenceReferenceReadModel) => ({
        label: `${ref.name} (${ref.uniqueCode})`,
        value: ref.uniqueCode
      })),
    [referencesQuery.data]
  );

  const referenceSelectOptions: SelectOption[] = useMemo(
    () =>
      referenceOptions.map((opt) => ({
        value: opt.value,
        label: opt.label
      })),
    [referenceOptions]
  );

  const formSchema = getAddPerformanceSchema(t, isProductReferenceEnabled);

  const form = useForm({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      companyUniqueCode: '',
      userUniqueCode: null,
      amount: undefined,
      realizedAt: undefined,
      label: '',
      targetReference: undefined,
      referenceUniqueCode: undefined,
      referenceQuantity: undefined
    }
  });

  useEffect(() => {
    if (!fixedCompany) return;
    const option = { label: fixedCompany.name, value: fixedCompany.uniqueCode };
    setSelectedCompanyOption(option);
    setSelectedCompanyId(fixedCompany.id);
    setCompanyQuery(fixedCompany.name);
    form.setValue('companyUniqueCode', fixedCompany.uniqueCode);
    form.setValue('userUniqueCode', null);
    if (isTargetLogic) form.setValue('targetReference', undefined);
  }, [fixedCompany, form, isTargetLogic]);

  const addPerformanceMutation = useApiAdminperformancesPost({
    mutation: {
      onSuccess: () => {
        toast.success(t('admin.performances.add.success'));
        form.reset(
          {
            companyUniqueCode: isCompanyFixed ? (fixedCompany?.uniqueCode ?? '') : '',
            userUniqueCode: null,
            amount: undefined,
            realizedAt: undefined,
            label: '',
            targetReference: undefined,
            referenceUniqueCode: undefined,
            referenceQuantity: undefined
          },
          { keepDefaultValues: false }
        );
        if (!isCompanyFixed) {
          setSelectedCompanyOption(null);
          setSelectedCompanyId(null);
          setCompanyQuery('');
        }
        onSuccess();
      },
      onError: (error) => {
        showOrvalErrorToast(error, t('toast.error.label'));
      }
    }
  });

  function onSubmit(data: z.infer<typeof formSchema>) {
    addPerformanceMutation.mutate({
      data: {
        companyUniqueCode: data.companyUniqueCode,
        userUniqueCode: data.userUniqueCode ?? undefined,
        amount: isProductReferenceEnabled ? (data.amount ?? 0) : data.amount,
        realizedAt: toUtcStartOfDay(data.realizedAt).toISOString(),
        label: data.label,
        targetReference: data.targetReference ?? undefined,
        referenceUniqueCode: data.referenceUniqueCode ?? undefined,
        referenceQuantity: data.referenceQuantity ?? undefined
      }
    });
  }

  return (
    <Form {...form} schema={formSchema}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="companyUniqueCode"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('admin.performances.add.form.company')}</FormLabel>
              <FormControl>
                <div className={isCompanyFixed ? 'pointer-events-none' : undefined}>
                  <ComboboxInput
                    placeholder={t('admin.performances.add.form.company.placeholder')}
                    immediate={true}
                    options={companyOptions}
                    input={companyQuery}
                    onInputChange={setCompanyQuery}
                    value={selectedCompanyOption}
                    onValueChange={(option) => {
                      if (isCompanyFixed) return;
                      setSelectedCompanyOption(option);
                      const companyDataList = (companiesQuery.data?.data ??
                        []) as CompanyCompanyCollectionReadModel[];
                      setSelectedCompanyId(
                        option
                          ? (companyDataList.find((c) => c.uniqueCode === option.value)?.id ?? null)
                          : null
                      );
                      field.onChange(option?.value ?? '');
                      form.setValue('userUniqueCode', null);
                      if (isTargetLogic) form.setValue('targetReference', undefined);
                    }}
                    loading={companiesQuery.isLoading}
                    className={fieldState.error ? 'border-destructive' : undefined}
                    disabled={isCompanyFixed}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="userUniqueCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('admin.performances.add.form.beneficiary')}</FormLabel>
              <FormControl>
                <SelectDropdown
                  options={beneficiarySelectOptions}
                  loading={usersQuery.isLoading}
                  disabled={!selectedCompanyName}
                  placeholder={t('admin.performances.add.form.beneficiary.placeholder')}
                  defaultValue={field.value ?? undefined}
                  allowDeselect={true}
                  onChange={(option) => {
                    field.onChange(option?.value ?? null);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="amount"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('admin.performances.add.form.amount')}</FormLabel>
              <FormControl>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={
                    typeof field.value === 'string' || typeof field.value === 'number'
                      ? field.value
                      : ''
                  }
                  onChange={(event) => field.onChange(event.target.value)}
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="realizedAt"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('admin.performances.add.form.realized.at')}</FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      className={cn(
                        'pl-3 text-left font-normal',
                        !field.value && 'text-muted-foreground'
                      )}
                    >
                      {field.value ? (
                        format(field.value, 'PPP')
                      ) : (
                        <span>{t('admin.performances.add.form.realized.at.pick')}</span>
                      )}
                      <CalendarIcon className="ml-auto w-4 h-4 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-auto" align="start">
                  <Calendar
                    mode="single"
                    selected={field.value ?? undefined}
                    onSelect={field.onChange}
                    disabled={(date) => date > new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />

        {isTargetLogic && (
          <FormField
            control={form.control}
            name="targetReference"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('admin.performances.add.form.target.reference')}</FormLabel>
                <FormControl>
                  <SelectDropdown
                    options={targetSelectOptions}
                    loading={false}
                    disabled={!selectedCompanyId || targetOptions.length === 0}
                    placeholder={t('admin.performances.add.form.target.reference.placeholder')}
                    defaultValue={field.value ?? undefined}
                    allowDeselect={true}
                    onChange={(option) => {
                      field.onChange(option?.value ?? undefined);
                    }}
                  />
                </FormControl>
                <FormMessage />
                {selectedCompanyId && targetOptions.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    {t('admin.performances.add.form.target.reference.empty')}
                  </p>
                ) : null}
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="label"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('admin.performances.add.form.label')}</FormLabel>
              <FormControl>
                <Textarea
                  placeholder={t('admin.performances.add.form.label.placeholder')}
                  rows={3}
                  className="resize-none"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {isProductReferenceEnabled && (
          <>
            <FormField
              control={form.control}
              name="referenceUniqueCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('admin.performances.add.form.reference.product')}</FormLabel>
                  <FormControl>
                    <SelectDropdown
                      options={referenceSelectOptions}
                      loading={referencesQuery.isLoading}
                      placeholder={t('admin.performances.add.form.reference.product.placeholder')}
                      defaultValue={field.value ?? undefined}
                      allowDeselect={true}
                      onChange={(option) => {
                        field.onChange(option?.value ?? undefined);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="referenceQuantity"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>{t('admin.performances.add.form.reference.quantity')}</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder="0"
                      value={
                        typeof field.value === 'string' || typeof field.value === 'number'
                          ? field.value
                          : ''
                      }
                      onChange={(event) => field.onChange(event.target.value)}
                      error={!!fieldState.error}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}

        <Button
          type="submit"
          rounded="full"
          loading={addPerformanceMutation.isPending}
          disabled={!form.formState.isValid}
          className="w-full bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
        >
          <Plus className="mr-2 w-4 h-4" />
          {t('common.add.performance')}
        </Button>
      </form>
    </Form>
  );
};
